import React, { createContext, useReducer, useCallback } from "react";
import axios from "../../axios-orders";
import {
  OrderInvitationContextType,
  OrderInvitationState,
  ProviderProps,
} from "../types";

// Initial state
const initialState: OrderInvitationState = {
  invites: [],
  isLoading: false,
  error: null,
  order: null,
};

// Create context
export const OrderInvitationContext = createContext<OrderInvitationContextType>(
  {
    ...initialState,
    addUserToInvites: () => {},
    createOrderInvitation: async () => {},
    cleanUserInvites: () => {},
  }
);

// OrderInvitation reducer
const orderInvitationReducer = (
  state: OrderInvitationState,
  action: any
): OrderInvitationState => {
  switch (action.type) {
    case "ADD_USER_TO_INVITES":
      const newState = [...state.invites];
      const index = newState.findIndex((item) => {
        return item.materialId === action.invite.materialId;
      });

      if (index === -1) {
        newState.push(action.invite);
      } else {
        newState[index] = action.invite;
      }

      return {
        ...state,
        invites: newState,
        isLoading: false,
      };
    case "ORDER_INVITATION_START":
      return {
        ...state,
        error: null,
        isLoading: true,
      };
    case "ORDER_INVITATION_SUCCESS":
      return {
        ...state,
        order: action.order,
        error: null,
        isLoading: false,
      };
    case "ORDER_INVITATION_FAILED":
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
    case "CLEAN_USER_INVITES":
      return {
        ...state,
        invites: [],
      };
    default:
      return state;
  }
};

// Provider component
export const OrderInvitationProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(orderInvitationReducer, initialState);

  // Add user to invites action
  const addUserToInvites = useCallback((invite: any) => {
    dispatch({
      type: "ADD_USER_TO_INVITES",
      invite,
    });
  }, []);

  // Create order invitation action
  const createOrderInvitation = useCallback(
    async (email: string, orderId: string) => {
      dispatch({ type: "ORDER_INVITATION_START" });

      try {
        const response = await axios.get(
          `/user/invitation/order/${email}/${orderId}`
        );

        dispatch({
          type: "ORDER_INVITATION_SUCCESS",
          order: response.data,
        });
      } catch (error) {
        dispatch({
          type: "ORDER_INVITATION_FAILED",
          error,
        });
      }
    },
    []
  );

  // Clean user invites action
  const cleanUserInvites = useCallback(() => {
    dispatch({ type: "CLEAN_USER_INVITES" });
  }, []);

  return (
    <OrderInvitationContext.Provider
      value={{
        ...state,
        addUserToInvites,
        createOrderInvitation,
        cleanUserInvites,
      }}
    >
      {children}
    </OrderInvitationContext.Provider>
  );
};
